import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { TaskListItem, UserActivityStats } from "@relais/shared";
import { TASK_STATUS_LABELS_FR, TASK_STATUSES, TASK_TYPE_LABELS_FR } from "@relais/shared";
import { STATUS_ACCENT } from "@relais/design-system";
import { BackLink } from "../components/BackLink.js";
import { useActivityStats } from "../lib/activity.js";
import { useOffline } from "../lib/offline-context.js";
import { fetchMyTasks } from "../lib/api.js";
import { PROFIL_CARD } from "../lib/profil-theme.js";

export const Route = createFileRoute("/profil_/activite")({
  component: ActivityPage,
});

function ActivityPage() {
  const { connectionState } = useOffline();
  const online = connectionState === "online";
  const stats = useActivityStats();

  return (
    <div className="flex flex-1 flex-col gap-6 pb-2">
      <BackLink to="/profil">Profil</BackLink>
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Votre activité</h1>
      </header>

      {stats.status === "loading" && (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      )}
      {stats.status === "error" && !online && (
        <p className="text-sm text-muted-foreground">
          Votre activité n'est pas disponible hors connexion.
        </p>
      )}
      {stats.status === "error" && online && (
        <p className="text-sm text-destructive">Impossible de charger votre activité.</p>
      )}
      {stats.status === "ready" && <ActivityContent stats={stats.stats} />}
    </div>
  );
}

function ActivityContent({ stats }: { stats: UserActivityStats }) {
  const completed = stats.byStatus.completed ?? 0;

  // Nouveau relais : aucune mission → ligne amicale, pas de graphe vide.
  if (stats.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Vos missions apparaîtront ici après votre première intervention.
      </p>
    );
  }

  const breakdown = TASK_STATUSES.map((s) => ({
    status: s,
    count: stats.byStatus[s] ?? 0,
  })).filter((b) => b.count > 0);

  return (
    <>
      {/* Hero : missions validées */}
      <section className={"rounded-xl p-5 " + PROFIL_CARD}>
        <p className="text-5xl font-semibold tabular-nums text-foreground">{completed}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {completed > 1 ? "missions validées" : "mission validée"}
          {" · "}
          {stats.total} au total
        </p>
      </section>

      {/* Répartition — légende à pastilles */}
      <section className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
        <h2 className="text-base font-medium text-foreground">Répartition</h2>
        <ul className="space-y-2.5">
          {breakdown.map((b) => (
            <li key={b.status} className="flex items-center gap-3">
              <span
                className={"size-2.5 shrink-0 rounded-full " + STATUS_ACCENT[b.status].dot}
                aria-hidden
              />
              <span className="flex-1 text-sm text-foreground">
                {TASK_STATUS_LABELS_FR[b.status]}
              </span>
              <span className="text-sm font-medium tabular-nums text-muted-foreground">
                {b.count}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <RecentMissions />
    </>
  );
}

function RecentMissions() {
  const [tasks, setTasks] = useState<TaskListItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMyTasks().then(
      (list) => !cancelled && setTasks(list),
      () => !cancelled && setFailed(true),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed || tasks === null || tasks.length === 0) return null;

  const recent = [...tasks]
    .sort((a, b) =>
      a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0,
    )
    .slice(0, 5);

  return (
    <section className="space-y-3">
      <h2 className="text-base font-medium text-foreground">Dernières missions</h2>
      <ul className="divide-y rounded-xl border">
        {recent.map((t) => (
          <li key={t.id}>
            <Link
              to="/tasks/$taskId"
              params={{ taskId: t.id }}
              className="block px-4 py-3 hover:bg-muted"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {TASK_TYPE_LABELS_FR[t.type]} · {t.project_snapshot?.name ?? t.project_id}
                </span>
                <span
                  className={
                    "shrink-0 rounded px-2 py-0.5 text-xs font-medium " +
                    STATUS_ACCENT[t.status].badge
                  }
                >
                  {TASK_STATUS_LABELS_FR[t.status]}
                </span>
              </div>
              {t.scheduled_at && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {new Date(t.scheduled_at).toLocaleString("fr-FR")}
                </div>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
